/** Settings page — profile, preferences, account, and danger zone sections. */
import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { AlertTriangle, Brain, KeyRound, Send, Settings2, Shield, UserRound } from 'lucide-react';
import { PageContainer } from '@/interfaces/learner/ui';
import ProfileSection from './ProfileSection';
import PreferencesSection from './PreferencesSection';
import AccountSection from './AccountSection';
import SessionsSection from './SessionsSection';
import DangerZoneSection from './DangerZoneSection';
import { MemorySection } from './MemorySection';
import InvitesSection from './InvitesSection';

const EASE = [0.16, 1, 0.3, 1] as const;
const SECTION_SCROLL_MARGIN = 'calc(var(--sticky-top-offset) + 8px)';

const SETTINGS_SECTIONS = [
  {
    id: 'profile',
    label: 'Profile',
    description: 'Identity and contact details',
    icon: UserRound,
  },
  {
    id: 'preferences',
    label: 'Preferences',
    description: 'Appearance and locale controls',
    icon: Settings2,
  },
  {
    id: 'account',
    label: 'Account',
    description: 'Security and login settings',
    icon: Shield,
  },
  {
    id: 'sessions',
    label: 'Sessions',
    description: 'Active devices and sign-outs',
    icon: KeyRound,
  },
  {
    id: 'memory',
    label: 'Memory',
    description: 'What AI remembers about you',
    icon: Brain,
  },
  {
    id: 'invites',
    label: 'Invites',
    description: 'Invite friends to Formency',
    icon: Send,
  },
  {
    id: 'danger-zone',
    label: 'Danger Zone',
    description: 'Irreversible account actions',
    icon: AlertTriangle,
  },
] as const;

type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number]['id'];

export default function SettingsPage() {
  const [activeSectionId, setActiveSectionId] = useState<SettingsSectionId>('profile');
  const sectionRefs = useRef<Record<SettingsSectionId, HTMLElement | null>>({
    profile: null,
    preferences: null,
    account: null,
    sessions: null,
    memory: null,
    invites: null,
    'danger-zone': null,
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const mostVisible = visible[0];
        if (!mostVisible) return;
        const sectionId = mostVisible.target.getAttribute(
          'data-settings-section-id'
        ) as SettingsSectionId | null;
        if (sectionId) setActiveSectionId(sectionId);
      },
      {
        threshold: [0.1, 0.25, 0.5, 0.75, 1],
        rootMargin: '-20% 0px -60% 0px',
      }
    );

    const nodes = Object.values(sectionRefs.current).filter((node): node is HTMLElement =>
      Boolean(node)
    );
    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, []);

  const sectionRef =
    (id: SettingsSectionId) =>
    (node: HTMLElement | null): void => {
      sectionRefs.current[id] = node;
    };

  function handleSectionNav(id: SettingsSectionId) {
    setActiveSectionId(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <PageContainer className="relative w-full pb-24">
      <section className="relative" style={{ paddingTop: 'var(--page-top-spacing)' }}>
        {/* ── Page title ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="min-w-0"
        >
          <p
            className="text-eyebrow"
            style={{
              color: 'var(--color-accent)',
              marginBottom: 10,
            }}
          >
            Learner Workspace
          </p>

          <h1
            className="text-hero-title"
            style={{
              maxWidth: '14ch',
            }}
          >
            Settings
          </h1>

          <p
            className="text-body-muted mt-3"
            style={{
              maxWidth: '62ch',
            }}
          >
            Personalise your profile, controls, and account safeguards in one place. The layout
            mirrors the new big-canvas learner style so the shell and page feel like one system.
          </p>

          <nav
            className="mt-5 flex gap-2 overflow-x-auto pb-1 learner-double:hidden"
            aria-label="Settings sections"
          >
            {SETTINGS_SECTIONS.map((item) => {
              const isActive = activeSectionId === item.id;
              return (
                <a
                  key={item.id}
                  href={`#settings-${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionNav(item.id);
                  }}
                  aria-current={isActive ? 'true' : undefined}
                  className={[
                    'shrink-0 rounded-full px-3.5 py-2 text-sm font-medium transition-all duration-150',
                    isActive
                      ? ''
                      : 'hover:bg-[var(--color-surface-muted)] hover:border-[var(--color-border-strong)]',
                  ].join(' ')}
                  style={{
                    color: isActive ? 'var(--color-accent-text)' : 'var(--color-text-secondary)',
                    background: isActive
                      ? 'var(--color-accent-subtle)'
                      : 'var(--color-surface-solid)',
                    border: `1px solid ${
                      isActive ? 'var(--color-accent-outline)' : 'var(--color-border)'
                    }`,
                    boxShadow: isActive ? 'var(--control-shadow-subtle)' : 'none',
                  }}
                >
                  {item.label}
                </a>
              );
            })}
          </nav>
        </motion.div>

        <div className="mt-8 learner-double:mt-10 learner-double:grid learner-double:grid-cols-[250px_minmax(0,1fr)] learner-double:gap-10">
          {/* Desktop section rail */}
          <nav
            className="hidden learner-double:block learner-double:self-start"
            aria-label="Settings sections"
            style={{
              position: 'sticky',
              top: 'calc(var(--sticky-top-offset) + 6px)',
              maxHeight: 'calc(100vh - var(--sticky-top-offset) - 20px)',
            }}
          >
            <div
              className="rounded-[24px] p-3.5"
              style={{
                background: 'var(--color-surface)',
                backdropFilter: 'blur(14px) saturate(1.15)',
                WebkitBackdropFilter: 'blur(14px) saturate(1.15)',
                border: '1px solid var(--color-surface-border)',
                boxShadow: 'var(--surface-shadow-glass-rest)',
                maxHeight: '100%',
                overflowY: 'auto',
              }}
            >
              <div className="space-y-1.5">
                {SETTINGS_SECTIONS.map((item) => {
                  const isActive = activeSectionId === item.id;
                  const Icon = item.icon;
                  return (
                    <a
                      key={item.id}
                      href={`#settings-${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSectionNav(item.id);
                      }}
                      aria-current={isActive ? 'true' : undefined}
                      className={[
                        'block rounded-xl px-3 py-2.5 transition-all duration-150',
                        isActive
                          ? ''
                          : 'hover:bg-[var(--color-surface-muted)] hover:border-[var(--color-surface-border)]',
                      ].join(' ')}
                      style={{
                        color: isActive ? 'var(--color-text)' : 'var(--color-text-muted)',
                        background: isActive ? 'var(--color-accent-subtle)' : 'transparent',
                        border: isActive
                          ? '1px solid var(--color-accent-outline)'
                          : '1px solid transparent',
                        boxShadow: isActive ? 'var(--control-shadow-subtle)' : 'none',
                      }}
                    >
                      <span className="flex items-start gap-2.5">
                        <span
                          className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-md"
                          style={{
                            background: isActive
                              ? 'var(--color-accent-wash-strong)'
                              : 'transparent',
                            color: isActive
                              ? 'var(--color-accent-text)'
                              : 'var(--color-text-muted)',
                          }}
                        >
                          <Icon size={13} strokeWidth={2} />
                        </span>
                        <span>
                          <span className="text-list-title block">{item.label}</span>
                          <span
                            className="text-list-meta-muted block"
                            style={{
                              maxWidth: '22ch',
                            }}
                          >
                            {item.description}
                          </span>
                        </span>
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Settings content */}
          <div className="space-y-14 learner-double:space-y-16">
            <section
              id="settings-profile"
              ref={sectionRef('profile')}
              data-settings-section-id="profile"
              style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }}
            >
              <ProfileSection />
            </section>

            <section
              id="settings-preferences"
              ref={sectionRef('preferences')}
              data-settings-section-id="preferences"
              style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }}
            >
              <PreferencesSection />
            </section>

            <section
              id="settings-account"
              ref={sectionRef('account')}
              data-settings-section-id="account"
              style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }}
            >
              <AccountSection />
            </section>

            <section
              id="settings-sessions"
              ref={sectionRef('sessions')}
              data-settings-section-id="sessions"
              style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }}
            >
              <SessionsSection />
            </section>

            <section
              id="settings-memory"
              ref={sectionRef('memory')}
              data-settings-section-id="memory"
              style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }}
            >
              <MemorySection />
            </section>

            <section
              id="settings-invites"
              ref={sectionRef('invites')}
              data-settings-section-id="invites"
              style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }}
            >
              <InvitesSection />
            </section>

            <section
              id="settings-danger-zone"
              ref={sectionRef('danger-zone')}
              data-settings-section-id="danger-zone"
              style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }}
            >
              <DangerZoneSection />
            </section>
          </div>
        </div>
      </section>
    </PageContainer>
  );
}
